
import React, { useContext, useEffect, useState } from 'react'

import { Gift, Minus, PersonSimpleThrow, Plus } from '@phosphor-icons/react'
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react'
import { useInView } from 'react-intersection-observer'

import StarRating from '@components/common/StarRating'
import UseWindowSize from '@components/helpers/UseWindowSize'
import ShareInSocialMedia from '@components/common/ShareInSocialMedia'
import Spacer from '@components/common/Spacer'
import HighlightBadge from '@components/common/HighlightBadge'
import ColorPanelDynamic from '../common/ColorPanelDynamic'
import SizePanelDynamic from '../common/SizePanelDynamic'
import ShopButtons from './ShopButtons'

import { StoreContext } from "@context/StoreContext"
import { productFormattedPrice } from "@utils"
import QuickBuyContext from '@context/QuickBuyContext'
import { getProductRating } from '@utils'

const ProductDetails = ({data, ratings, isLoading}) => {
    const { checkout } = React.useContext(StoreContext)

    const [size, setSize] = useState('');
    const [sizes, setSizes] = useState([]);
    const { setForceHideMenuBar } = useContext(QuickBuyContext)

    const {
        totalReviews,
        average,
    } = getProductRating(ratings)

    const { ref, inView } = useInView({
        threshold: 0.1,
        rootMargin: '0px 0px 250px 0px',
    })
    const { width } = UseWindowSize()

    
    useEffect(() => {
      setForceHideMenuBar(!inView)
    }, [inView])
    
    const mockCrumbs = [
        {label: 'women', path: '/products'},
        {label: 'tops', path: '/products'},
        {label: 'two piece', path: '/products'},
    ]

    const extractDetails = (html) => {
        if (typeof window === 'undefined') {
            return null;
        }
        // Create a temporary DOM element
        const tempDiv = document.createElement('div');
        
        // Set the inner HTML to the provided HTML string
        tempDiv.innerHTML = html;
      
        // Select all the headers
        const headers = tempDiv.querySelectorAll('.product-accordion__header');
      
        // Find the header that contains "Details"
        let detailsContent = '';
      
        headers.forEach(header => {
          if (header.textContent.trim() === "Details") {
            // Get the next sibling (the content div)
            const content = header.nextElementSibling; // This should be the details content div
            if (content) {
              // Find the <ul> within the content
              const ul = content.querySelector('ul');
              if (ul) {
                // Add the classes to the <ul>
                ul.classList.add('list-disc', 'px-6');
              }
              detailsContent = content.innerHTML; // Get the HTML of the content
            }
          }
        });
      
        return detailsContent;
    };

    const extractSizeFitSection = (html) => {
        if (typeof window === 'undefined') {
            return null;
        }
        // Create a temporary DOM element
        const tempDiv = document.createElement('div');
        
        // Set the inner HTML to the provided HTML string
        tempDiv.innerHTML = html;
      
        // Select the header for "Size & Fit"
        const sizeFitHeader = Array.from(tempDiv.querySelectorAll('.product-accordion__header')).find(header => 
          header.textContent.trim() === "Size & Fit"
        );
      
        if (sizeFitHeader) {
          // Get the next sibling (the content div)
          const content = sizeFitHeader.nextElementSibling; // This should be the content div
          if (content) {
            // Modify the <ul> if it's present
            const ul = content.querySelector('ul');
            if (ul) {
              ul.classList.add('list-disc', 'px-6'); // Add classes to the <ul>
            }
            return content.innerHTML; // Return the HTML content
          }
        }
        return null; // Return null if not found
    };

    const extractShippingSection = (html) => {
        if (typeof window === 'undefined') {
            return null;
        }
        // Create a temporary DOM element
        const tempDiv = document.createElement('div');
        
        // Set the inner HTML to the provided HTML string
        tempDiv.innerHTML = html;
      
        // Select the header for "Shipping"
        const shippingHeader = Array.from(tempDiv.querySelectorAll('.product-accordion__header')).find(header => 
          header.textContent.trim() === "Shipping"
        );
      
        if (shippingHeader) {
          // Get the next sibling (the content div)
          const content = shippingHeader.nextElementSibling; // This should be the content div

          const rawHtml = content.innerHTML;

          // Detect links within the HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(rawHtml, "text/html");
          const links = doc.querySelectorAll("a"); // Get all <a> tags

          links.forEach(link => {
            // Directly modify the style of the <a> tag
            link.style.color = "black"; // Change color of the link text
            link.style.fontWeight = "600";
          });

          // Inject the styled text back into the container
          content.innerHTML = doc.body.innerHTML;

          if (content) {
            return content.innerHTML; // Return the HTML content
          }
        }
        return null; // Return null if not found
    };

    const extractReturnsSection = (html) => {
        if (typeof window === 'undefined') {
            return null;
        }
        // Create a temporary DOM element
        const tempDiv = document.createElement('div');
        
        // Set the inner HTML to the provided HTML string
        tempDiv.innerHTML = html;
      
        // Select the header for "Returns"
        const returnsHeader = Array.from(tempDiv.querySelectorAll('.product-accordion__header')).find(header => 
          header.textContent.trim() === "Returns"
        );
      
        if (returnsHeader) {
          // Get the next sibling (the content div)
          const content = returnsHeader.nextElementSibling; // This should be the content div

          const rawHtml = content.innerHTML;

          // Detect links within the HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(rawHtml, "text/html");
          const links = doc.querySelectorAll("a"); // Get all <a> tags

          links.forEach(link => {
            // Directly modify the style of the <a> tag
            link.style.color = "black"; // Change color of the link text
            link.style.fontWeight = "600";
          });

          // Inject the styled text back into the container
          content.innerHTML = doc.body.innerHTML;

          if (content) {
            return content.innerHTML; // Return the HTML content
          }
        }
        return null; // Return null if not found
    };

    const extractShortDescription = (html) => {
        if (typeof window === 'undefined') {
            return null;
        }
    
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
    
        const shortDescription = tempDiv.querySelector('.product_short-description');
    
        if (shortDescription) {
            // Extract only the text content, no HTML tags
            return shortDescription.textContent.trim();
        }
    
        return null;
    };
    
    

    const badgeItems = data?.tags?.filter(item => item.startsWith("badge_"));
    const details = extractDetails(data?.descriptionHtml);
    const sizeFitContent = extractSizeFitSection(data?.descriptionHtml);
    const shippingContent = extractShippingSection(data?.descriptionHtml);
    const returnsContent = extractReturnsSection(data?.descriptionHtml);
    const returnsContentFinal = returnsContent?.replace(/blackboughswim/g, 'checkout.blackboughswim');

    const swataches = data?.metafields?.find(item => item.key === "swatch");
    const parsedSwatches = swataches?.value !== undefined ? JSON.parse(swataches?.value) : [];
    
    const models = data?.metafields?.find(item => item.key === "models");
    
    const parsedModels = models?.value !== undefined ? JSON.parse(models?.value) : [];

    const shortDescription = extractShortDescription(data?.descriptionHtml);
    

    const isDiscounted = data?.priceRangeV2?.minVariantPrice?.amount !== data?.variants[0]?.compareAtPrice && data?.variants[0]?.compareAtPrice !== undefined ? true : false;
    const hasComingSoon = data?.collections?.some(collection => collection?.handle === "coming-soon");

    const isFreebie = data?.title?.split(" / ")?.[0]?.toLowerCase().includes("free");


    const calculateDiscount = (compareAtPrice, price) => {
        if (!compareAtPrice || compareAtPrice <= price) return 0;
        
        return Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
    }

    useEffect(() => {
        const fetchInventory = async () => {
          const response = await fetch("https://" + process.env.GATSBY_MYSHOPIFY_URL + "/api/2025-01/graphql.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
            },
            body: JSON.stringify({
              query: `
                query GetProductInventory($handle: String!) {
                  productByHandle(handle: $handle) {
                    variants(first: 50) {
                      edges {
                        node {
                          title
                          availableForSale
                          quantityAvailable
                          id
                        }
                      }
                    }
                  }
                }
              `,
              variables: { handle: data?.handle },
            }),
          });
    
          const result = await response.json();
          const sizes = result?.data?.productByHandle?.variants?.edges?.map(variant => variant?.node) || [];

          setSizes(sizes)
        };
    
        fetchInventory();
      }, [data]);

  return (
    <React.Fragment>
      <section className="w-full px-2">
        {/*width > 768 && (
          <section className="w-full flex justify-between">
            <>
              <BreadCrumbs crumbs={mockCrumbs} />
              <section className="flex flex-nowrap gap-x-2">
                <HeartIcon size={20} onClick={() => {}} />
                <SoftButton label={"ADD TO WISHLIST"} onClick={() => {}} />
              </section>
            </>
          </section>
        )*/}

        {!inView && (
          <Transition as="div"
            show={!inView} // Control visibility using the `inView` state
            enter="transition-transform duration-500"
            enterFrom="translate-y-10 opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="transition-transform duration-500"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="translate-y-10 opacity-0"
          >
            <ShopButtons data={data} price={data?.priceRangeV2?.minVariantPrice?.amount} size={size} variants={sizes} stickyMode hideProductDetails={width < 500} />
          </Transition>
        )}

        <section className="w-full pt-2">
          <h1 className="font-normal">{data?.title?.split(" / ")?.[0]}</h1>
          <div className="flex gap-2 uppercase font-medium text-[#374151] text-sm m-2">
            {
                isDiscounted ? 
                    <span className='badge'>{`SAVE ${calculateDiscount(data?.variants?.[0]?.compareAtPrice, data?.priceRangeV2?.minVariantPrice?.amount)} %`}</span>
                    : badgeItems?.length > 0 ? <span className='badge'>{badgeItems?.[0]?.replace("badge_", "")?.replace("-", " ")}</span> : null
            }
          </div>
          <div className="flex items-center justify-between mt-4 mb-2">
            <div className="flex gap-x-4 font-medium">
                <span>
                    {
                        checkout?.cost?.totalAmount?.currencyCode ?
                            productFormattedPrice(
                                checkout?.cost?.totalAmount?.currencyCode,
                                data?.priceRangeV2?.minVariantPrice?.amount
                            )
                            : null
                    }
                </span>
              {isDiscounted && <span className="line-through text-[#D1D1D1]">
                                {
                                    checkout?.cost?.totalAmount?.currencyCode ?
                                        productFormattedPrice(
                                            checkout?.cost?.totalAmount?.currencyCode,
                                            data?.variants[0]?.compareAtPrice
                                        )
                                        : null
                                }</span>}
            </div>

            <div className="flex gap-x-2">
                { 
                    !isLoading && 
                        <>
                            <StarRating totalStars={1} initalRating={1} readOnly={true} />
                            <span>{isNaN(average) ? "0" : average.toFixed(1)}</span>
                            <span className="text-[#D1D1D1]">({totalReviews ? totalReviews : "0"})</span>
                            <span className="text-secondary">|</span>
                        </>
                }
              <ShareInSocialMedia product={data} className="mr-1 z-40" />
            </div>
          </div>
        </section>

        <Spacer />

        <div className="px-2">
            <ColorPanelDynamic data={parsedSwatches} title={data?.title?.split(" / ")?.[1]?.replace("FINAL SALE", "")} selected={data?.handle} />
            <Spacer marginTop="mt-5" margin="mt-5 mx-1" />
            <SizePanelDynamic productName={data?.title?.split(" / ")?.[0]} sizes={sizes} setSize={setSize} hasComingSoon={hasComingSoon} isFreebie={isFreebie} />
        </div>

        <div className="text-[#3D3D3D]">
            
          <p className="px-2 text-justify text-sm py-4">{shortDescription}</p>
          <div className="mt-3 px-3 space-y-6">
            {
                parsedModels && 
                    <HighlightBadge
                        icon={
                            <PersonSimpleThrow
                                size={48}
                                weight="light"
                                className="icon-round-bg bg-gray-200 p-2"
                            />
                        }
                        title={"MODEL DETAILS"}
                        description={parsedModels?.[0]?.description}
                    />
            }
            
            <HighlightBadge
              icon={
                <Gift
                  size={48}
                  weight="light"
                  className="icon-round-bg bg-gray-200 p-2"
                />
              }
              title={`FREE SHIPPING ${process.env.GATSBY_STORE !== "PH" ? "OVER $100" : 'NATIONWIDE'}`}
              description={`No need to fuss over shipping fees for orders over ${process.env.GATSBY_STORE !== "PH" ? "$100" : '3500'}. We've got you!`}
            />
          </div>
        </div>

        {width > 768 && (
          <div ref={ref}>
            <ShopButtons data={data} price={data?.priceRangeV2?.minVariantPrice?.amount} size={size} variants={sizes} isFreebie={isFreebie} />
          </div>
        )}
        {
            /*
        
                <div className="my-8 px-4">
                <div className="text-[1rem]">COMPLETE THE LOOK</div>
                <div className="mt-4">
                    <ProductSwiper
                    products={MockProducts}
                    cardOptions={"no-tag no-wishlist"}
                    slidesPerView={3}
                    spaceBetween={8}
                    breakpoints={{
                        300: {
                        slidesPerView: 1,
                        },
                        500: {
                        slidesPerView: 1,
                        },
                        640: {
                        slidesPerView: 3,
                        },
                        900: {
                        slidesPerView: 3,
                        },
                        1024: {
                        slidesPerView: 3,
                        },
                    }}
                    />
                </div>
                </div>
            */
        }

        <div className="mt-8 w-full space-y-4 px-4 text-[#6D6D6D]">
          <Disclosure defaultOpen={true}>
            {({ open }) => (
              <>
                <DisclosureButton
                  className={"group flex w-full justify-between items-center my-6"}
                >
                  <span className="accordion-button">DETAILS</span>
                  {open ? (
                    <Minus size={16} weight="light" />
                  ) : (
                    <Plus size={16} weight="light" />
                  )}
                </DisclosureButton>
                <DisclosurePanel
                  transition
                  className="mt-6 origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0"
                >
                  <div>
                    <div
                        dangerouslySetInnerHTML={{ __html: details }}
                    />
                  </div>
                  
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
          <Disclosure>
            {({ open }) => (
              <>
                <DisclosureButton
                  className={"group flex w-full justify-between items-center pt-6 border-t border-t-gray-300"}
                >
                  <span className="accordion-button">SIZE & FIT</span>
                  {open ? (
                    <Minus size={16} weight="light" />
                  ) : (
                    <Plus size={16} weight="light" />
                  )}
                </DisclosureButton>
                <DisclosurePanel
                  transition
                  className="mt-6 origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0"
                >
                    <div
                        dangerouslySetInnerHTML={{ __html: sizeFitContent }}
                    />
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
          <Disclosure>
            {({ open }) => (
              <>
                <DisclosureButton
                  className={"group flex w-full justify-between items-center pt-6 border-t border-t-gray-300"}
                >
                  <span className="accordion-button">SHIPPING</span>
                  {open ? (
                    <Minus size={16} weight="light" />
                  ) : (
                    <Plus size={16} weight="light" />
                  )}
                </DisclosureButton>
                <DisclosurePanel
                  transition
                  className="mt-6 origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0"
                >
                    <div
                        dangerouslySetInnerHTML={{ __html: shippingContent }}
                    />
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
          <Disclosure>
            {({ open }) => (
              <>
                <DisclosureButton
                  className={"group flex w-full justify-between items-center pt-6 border-t border-t-gray-300"}
                >
                  <span className="accordion-button">RETURNS</span>
                  {open ? (
                    <Minus size={16} weight="light" />
                  ) : (
                    <Plus size={16} weight="light" />
                  )}
                </DisclosureButton>
                <DisclosurePanel
                  transition
                  className="mt-6 origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0"
                >
                    <div
                        dangerouslySetInnerHTML={{ __html: returnsContentFinal }}
                    />
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        </div>
      </section>
    </React.Fragment>
  );
}

export default ProductDetails